// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * UserPreferencesLoader Component
 *
 * Loads user display preferences from the Practitioner resource on app startup
 * and applies them to the relevant contexts (theme, sidebar, etc.).
 * This ensures settings persist across devices and browsers.
 */

import { useEffect, useRef, useState } from 'react';
import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useAppTheme, type AppThemeId, type AppStyleId, type DarkModeTintId, type DarkModeBaseId } from '../contexts/AppThemeContext';
import { useThemeColor } from '../contexts/ThemeColorContext';
import { useSidebar, type SidebarMode } from '../contexts/SidebarContext';
import { getUserPreferences } from '../utils/userPreferences';

type ShadeType = 'light' | 'medium' | 'dark';

export function UserPreferencesLoader(): null {
  const medplum = useMedplum();
  const {
    setTheme,
    setStyle,
    setDarkModeTint,
    setDarkModeBase,
    availableThemes,
    availableStyles,
    availableDarkModeTints,
    availableDarkModeBases,
  } = useAppTheme();
  const { setThemeColor, setAIButtonColor } = useThemeColor();
  const { setMode: setSidebarMode } = useSidebar();
  const loadedRef = useRef(false);
  const [, setLoadAttempts] = useState(0);

  useEffect(() => {
    // Only load once
    if (loadedRef.current) return;

    const loadPreferences = async (): Promise<void> => {
      const profile = medplum.getProfile() as Practitioner | undefined;
      if (!profile?.id) {
        // Profile not ready yet, retry in a moment
        setTimeout(() => setLoadAttempts((prev) => prev + 1), 100);
        return;
      }

      try {
        // Fetch fresh Practitioner data to get latest extensions
        const freshPractitioner = await medplum.readResource('Practitioner', profile.id);
        const userPrefs = getUserPreferences(freshPractitioner);

        if (!userPrefs) {
          loadedRef.current = true;
          return;
        }

        // Apply saved display preferences
        if (userPrefs.appThemeId && availableThemes.some((t) => t.id === userPrefs.appThemeId)) {
          setTheme(userPrefs.appThemeId as AppThemeId);
        }
        if (userPrefs.appStyleId && availableStyles.some((s) => s.id === userPrefs.appStyleId)) {
          setStyle(userPrefs.appStyleId as AppStyleId);
        }
        if (userPrefs.darkModeTintId && availableDarkModeTints.some((t) => t.id === userPrefs.darkModeTintId)) {
          setDarkModeTint(userPrefs.darkModeTintId as DarkModeTintId);
        }
        if (userPrefs.darkModeBaseId && availableDarkModeBases.some((b) => b.id === userPrefs.darkModeBaseId)) {
          setDarkModeBase(userPrefs.darkModeBaseId as DarkModeBaseId);
        }
        if (userPrefs.headerThemeColorGroupId && userPrefs.headerThemeColorShade) {
          setThemeColor(userPrefs.headerThemeColorGroupId, userPrefs.headerThemeColorShade as ShadeType);
        }
        if (userPrefs.headerButtonColorId) {
          setAIButtonColor(userPrefs.headerButtonColorId);
        }
        if (userPrefs.sidebarMode) {
          setSidebarMode(userPrefs.sidebarMode as SidebarMode);
        }

        loadedRef.current = true;
      } catch (err) {
        console.warn('Failed to load user preferences from Practitioner:', err);
        loadedRef.current = true;
      }
    };

    void loadPreferences();
  }, [
    medplum,
    availableThemes,
    availableStyles,
    availableDarkModeTints,
    availableDarkModeBases,
    setTheme,
    setStyle,
    setDarkModeTint,
    setDarkModeBase,
    setThemeColor,
    setAIButtonColor,
    setSidebarMode,
  ]);

  return null;
}
